import { useState, useEffect } from "react";
import { useScheduler } from "../../hooks/useScheduler";
import { getUserData } from "../../../infrastructure/storage/tokenStorage";
import { MdSchedule } from "react-icons/md";
import { FiEdit2 } from "react-icons/fi";
import { MdPlayArrow } from "react-icons/md";
import UpdateCronModal from "./UpdateCronModel";
import ToastAlert from "../common/ToastAlert";

const CronSection = () => {
  const {
    getAllSchedules,
    updateScheduler,
    syncInfrastructure,
    syncPerformance,
    syncAlerts,
    syncInvoices,
    syncMonthlyCosts,
    syncBackup,
    schedules,
    loading,
    error,
  } = useScheduler();
  const [userRole, setUserRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [executingTask, setExecutingTask] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const userData = getUserData();
    setUserRole(userData?.role);
    getAllSchedules();
  }, []);

  const canUpdate = userRole === "SUPER_ADMIN" || userRole === "MANAGER";

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "Never";
    const date = new Date(dateTime);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      SUCCESS: "bg-green-500/20 text-green-400 border-green-500",
      FAILED: "bg-red-500/20 text-red-400 border-red-500",
      RUNNING: "bg-yellow-500/20 text-yellow-400 border-yellow-500",
      NEVER_RUN: "bg-gray-500/20 text-gray-400 border-gray-500",
    };
    const defaultStyle = "bg-gray-500/20 text-gray-400 border-gray-500";

    return (
      <span
        className={`px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium border ${styles[status] || defaultStyle}`}
      >
        {status || "NEVER_RUN"}
      </span>
    );
  };

  const handleUpdateClick = (taskName, currentCron) => {
    setSelectedTask({ taskName, currentCron });
    setIsModalOpen(true);
  };

  const handleConfirmUpdate = async (newCron) => {
    setIsUpdating(true);
    try {
      const result = await updateScheduler(selectedTask.taskName, newCron);
      if (result.success) {
        setIsModalOpen(false);
        setSelectedTask(null);
        await getAllSchedules();
        showToast("Schedule updated successfully!", "success");
      } else {
        showToast(result.message || "Failed to update schedule", "error");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExecuteTask = async (taskName) => {
    setExecutingTask(taskName);

    try {
      let result;
      switch (taskName) {
        case "infra":
          result = await syncInfrastructure();
          break;
        case "performance":
          result = await syncPerformance();
          break;
        case "alert":
          result = await syncAlerts();
          break;
        case "invoice":
          result = await syncInvoices();
          break;
        case "monthlyCost":
          const now = new Date();
          const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const year = prevMonth.getFullYear();
          const month = prevMonth.getMonth() + 1;
          result = await syncMonthlyCosts(year, month);
          break;
        case "backup":
          result = await syncBackup();
          break;
        default:
          showToast(`Unknown task: ${taskName}`, "error");
          setExecutingTask(null);
          return;
      }

      if (result && result.success) {
        const taskNames = {
          infra: "Infrastructure",
          performance: "Performance",
          alert: "Alerts",
          invoice: "Invoices",
          monthlyCost: "Monthly Cost",
          backup: "Backup",
        };
        const displayName = taskNames[taskName] || taskName;
        showToast(`${displayName} sync completed successfully!`, "success");
        await getAllSchedules();
      } else if (result && !result.success) {
        showToast(result.message || `Failed to sync ${taskName}`, "error");
      }
    } catch (err) {
      showToast(`Error syncing ${taskName}: ${err.message}`, "error");
    } finally {
      setExecutingTask(null);
    }
  };

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 5000);
  };

  if (loading && !schedules) {
    return (
      <div className="py-4 sm:py-6">
        <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <div className="text-center py-6 sm:py-8">
            <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 text-sm sm:text-base mt-2 sm:mt-3">Loading schedules...</p>
          </div>
        </div>
      </div>
    );
  }

  const scheduleList = schedules
    ? Object.keys(schedules)
        .filter((key) => key !== "__typename")
        .map((taskName) => ({
          taskName,
          cronExpression: schedules[taskName].cronExpression,
          lastExecution: schedules[taskName].lastExecution,
          lastStatus: schedules[taskName].lastStatus,
          nextExecution: schedules[taskName].nextExecution,
        }))
    : [];

  const getTaskDisplayName = (taskName) => {
    const names = {
      infra: "Sync Infrastructure",
      performance: "Sync Performance",
      alert: "Sync Alerts",
      invoice: "Sync Invoices",
      monthlyCost: "Sync Monthly Costs",
      backup: "Sync Backup",
    };
    return names[taskName] || taskName;
  };

  return (
    <div className="py-4 sm:py-6">
      {toast && (
        <ToastAlert
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-6 pb-2 sm:pb-3 border-b border-gray-700">
          <MdSchedule className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Scheduled Tasks</h3>
          {canUpdate && (
            <span className="ml-auto text-[10px] sm:text-xs text-gray-400">
              Only Super Admin and Manager can update
            </span>
          )}
        </div>

        {error && !toast && (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <p className="text-red-400 text-xs sm:text-sm">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Task</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium hidden md:table-cell">Cron</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium hidden lg:table-cell">Last Exec</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Status</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium hidden xl:table-cell">Next</th>
                {canUpdate && (
                  <th className="text-center py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Update</th>
                )}
                {canUpdate && (
                  <th className="text-center py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Run</th>
                )}
              </tr>
            </thead>
            <tbody>
              {scheduleList.map((task) => (
                <tr
                  key={task.taskName}
                  className="border-b border-gray-700/50 hover:bg-white/5 transition"
                >
                  <td className="py-2 sm:py-3 px-1 sm:px-2">
                    <span className="text-white font-medium text-xs sm:text-sm break-words">
                      {getTaskDisplayName(task.taskName)}
                    </span>
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2 hidden md:table-cell">
                    <code className="bg-gray-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-primary-cyan font-mono text-[10px] sm:text-xs break-all">
                      {task.cronExpression}
                    </code>
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2 text-gray-300 text-[10px] sm:text-xs hidden lg:table-cell">
                    {formatDateTime(task.lastExecution)}
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2">
                    {getStatusBadge(task.lastStatus)}
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2 text-gray-300 text-[10px] sm:text-xs hidden xl:table-cell">
                    {formatDateTime(task.nextExecution)}
                  </td>
                  {canUpdate && (
                    <td className="py-2 sm:py-3 px-1 sm:px-2 text-center">
                      <button
                        onClick={() => handleUpdateClick(task.taskName, task.cronExpression)}
                        className="text-primary-cyan hover:text-primary-cyan/80 transition p-1"
                        title="Update schedule"
                      >
                        <FiEdit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </td>
                  )}
                  {canUpdate && (
                    <td className="py-2 sm:py-3 px-1 sm:px-2 text-center">
                      <button
                        onClick={() => handleExecuteTask(task.taskName)}
                        disabled={executingTask === task.taskName}
                        className={`bg-primary-cyan hover:bg-primary-cyan/80 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition flex items-center gap-1 mx-auto ${
                          executingTask === task.taskName ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        title="Execute now"
                      >
                        {executingTask === task.taskName ? (
                          <>
                            <div className="inline-block w-2 h-2 sm:w-3 sm:h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="hidden sm:inline">Running...</span>
                          </>
                        ) : (
                          <>
                            <MdPlayArrow className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Execute</span>
                          </>
                        )}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {scheduleList.length === 0 && !loading && (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No schedules found</p>
          </div>
        )}
      </div>

      {selectedTask && (
        <UpdateCronModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTask(null);
          }}
          taskName={selectedTask.taskName}
          currentCron={selectedTask.currentCron}
          onUpdate={handleConfirmUpdate}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default CronSection;